import { useNavigate } from 'react-router-dom';
import { Table, Button, Space, Typography } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  useGetWorksheetsQuery,
  useDeleteWorksheetMutation,
} from '@/state/slices/reports';
import type { Worksheet } from '@/models/template';
import HeartLoader from '@/shared/ui/HeartLoader';
import { useConfirmDelete } from '../hooks/useConfirmDelete';
import { TemplateListToolbar } from '../TemplateListToolbar';
import { formatDate } from '../utils/formatDate';
import styles from './WorksheetList.module.css';

const { Text } = Typography;

export function WorksheetList() {
  const navigate = useNavigate();
  const { data, isLoading } = useGetWorksheetsQuery();
  const [deleteWorksheet] = useDeleteWorksheetMutation();

  const worksheets = data?.results ?? [];

  const handleDelete = useConfirmDelete('Worksheet', deleteWorksheet);

  const columns: ColumnsType<Worksheet> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      render: formatDate,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/templates/worksheets/${record.uuid}`)}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            aria-label="Delete"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <TemplateListToolbar kind="worksheet" count={data?.count} />
      <Table
        rowKey="uuid"
        columns={columns}
        dataSource={worksheets}
        loading={{
          spinning: isLoading,
          indicator: <HeartLoader size="sm" />,
        }}
        pagination={false}
        locale={{ emptyText: 'No worksheets found' }}
      />
    </div>
  );
}
